import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckIcon,
  QuestionMarkCircleIcon,
  NoSymbolIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import {
  ConnectorRegistryEntry,
  ConnectorConnection,
  ConnectorPermissionState,
  ConnectorToolDescriptor,
  ConnectorPermissionsResponse,
  connectorsAPI,
} from '../../services/api';
import ManageConnectionConnectingState from './ManageConnectionConnectingState';
import ByoConnectionBody from './ByoConnectionBody';
import type { ConnectResult } from './ConnectorsModalProvider';
import { NEEDS_RECONNECT_PILL } from './copy';

/**
 * ManageConnectionPanel — detail modal for a connected MCP integration.
 * Header (Enabled + MCP badges, Disconnect), description copy, feature
 * bullets, and a permission manager (global default + per-tool 3-state
 * segmented control with a collapse-after-3 "View more" affordance to
 * keep the modal compact for connectors with large tool catalogs).
 * Permission writes are optimistic; on PUT failure we revert and surface
 * a feedback banner.
 *
 *   role="dialog" + aria-modal + focus trap + Esc-dismiss preserved from
 *   the prior version so the screen-reader & keyboard story doesn't regress.
 */
export interface ManageConnectionPanelProps {
  entry: ConnectorRegistryEntry;
  // `null` when the connector exists in the registry but the user hasn't
  // connected it yet — e.g. deep-link to `?connector=heygen` from a
  // marketing email or saved URL. We render the Connect CTA body in that
  // case via <ManageConnectionConnectingState/> so the deep link does
  // what the URL implies.
  connection: ConnectorConnection | null;
  onClose: () => void;
  // Breadcrumb back — clicking "Connectors" in the header invokes this.
  // Optional so older test fixtures and standalone uses still work.
  onBack?: () => void;
  onDisconnect: () => void | Promise<void>;
  isDisconnecting?: boolean;
  feedback?: { kind: 'info' | 'warn'; text: string } | null;
  // Bring-your-own (connectMode 'connect') only: the outcome of coming back
  // from the service, and a refetch after disconnect.
  connectResult?: ConnectResult | null;
  onConnectionChange?: () => void | Promise<void>;
}

// Per-connector copy. Colocated rather than server-fetched — these are
// user-facing strings on the critical render path, and a one-line change
// shouldn't need a round-trip. Voice matches the rest of the app: "your
// AI coworker" (see Connectors page tagline + sidebar copy). Avoid
// vendor-derived phrasing — describe what the connector unlocks in our
// own words.
const CONNECTOR_COPY: Record<string, { description: string; features: string[] }> = {
  heygen: {
    description:
      "Make AI videos with avatars and voices. Once connected, ask your AI coworker to record a script with a chosen avatar, synthesize speech, or translate a video — it'll call HeyGen directly and drop the result into Files.",
    features: [
      'Generate videos from a script with an AI avatar of your choice',
      'Synthesize speech in natural voices across many languages',
      'Translate an existing video into another language with lip sync preserved',
      'Reuse or extend videos you generated earlier',
    ],
  },
};

const PERMISSION_OPTIONS: { value: ConnectorPermissionState; label: string }[] = [
  { value: 'always', label: 'Always allow' },
  { value: 'ask', label: 'Ask each time' },
  { value: 'never', label: 'Never allow' },
];

const PERMISSION_LABEL: Record<ConnectorPermissionState, string> = {
  always: 'Always allow',
  ask: 'Ask each time',
  never: 'Never allow',
};

const PermissionGlyph: React.FC<{
  value: ConnectorPermissionState;
  current: ConnectorPermissionState;
  onSelect: (next: ConnectorPermissionState) => void;
  ariaLabelPrefix: string;
}> = ({ value, current, onSelect, ariaLabelPrefix }) => {
  const Icon =
    value === 'always' ? CheckIcon : value === 'ask' ? QuestionMarkCircleIcon : NoSymbolIcon;
  const active = current === value;
  const activeTone =
    value === 'always'
      ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/40'
      : value === 'ask'
        ? 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/40'
        : 'bg-red-500/20 text-red-300 ring-1 ring-red-500/40';
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-label={`${ariaLabelPrefix}: ${PERMISSION_LABEL[value]}`}
      aria-pressed={active}
      data-testid={`perm-${ariaLabelPrefix}-${value}`}
      className={`inline-flex items-center justify-center w-8 h-8 max-md:w-11 max-md:h-11 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
        active ? activeTone : 'text-white/40 hover:text-white/70 hover:bg-white/5'
      }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
};

const PermissionSegmented: React.FC<{
  value: ConnectorPermissionState;
  onChange: (next: ConnectorPermissionState) => void;
  ariaLabelPrefix: string;
}> = ({ value, onChange, ariaLabelPrefix }) => (
  <div
    role="group"
    aria-label={`${ariaLabelPrefix} permission`}
    className="inline-flex items-center gap-1 p-0.5 rounded-lg bg-white/5 border border-white/10"
  >
    {(['always', 'ask', 'never'] as ConnectorPermissionState[]).map((v) => (
      <PermissionGlyph
        key={v}
        value={v}
        current={value}
        onSelect={onChange}
        ariaLabelPrefix={ariaLabelPrefix}
      />
    ))}
  </div>
);

const ManageConnectionPanel: React.FC<ManageConnectionPanelProps> = ({
  entry,
  connection,
  onClose,
  onBack,
  onDisconnect,
  isDisconnecting,
  feedback,
  connectResult,
  onConnectionChange,
}) => {
  // Bring-your-own connectors (the customer's own Stripe / Notion / Linear)
  // render their own body; everything below this flag is HeyGen's path,
  // unchanged.
  const isConnectMode = entry.connectMode === 'connect';
  const byoStatus = isConnectMode && connection ? connection.status : null;
  // Universal-key connectors (server holds one API key, user just flips
  // a flag) drive their lifecycle through ManageConnectionConnectingState's
  // GET /status path rather than any legacy connect/disconnect flow. We
  // distinguish them by inspecting the status field on the connection row.
  //
  // For these the panel renders ConnectingState which handles all three
  // states itself (and shows its own status badge + Enable/Disable CTAs).
  const isUniversalKey =
    connection !== null &&
    (connection.status === 'enabled' ||
      connection.status === 'disabled' ||
      connection.status === 'not_enabled');
  // OAuth "connected" body renders only for true OAuth connections. Kept
  // for any future OAuth-shaped connector; today's only registry entry
  // (heygen) is universal-key, so this path effectively never fires.
  const isConnected =
    !isConnectMode && connection !== null && !isUniversalKey && connection.status === 'connected';
  // Connectors that opt out of per-tool permissions (universal-key or
  // future apikey-shaped OAuth replacements) skip the per-tool grid and
  // its companion fetch. Keeps the panel quiet and avoids a guaranteed
  // 400 from GET /:id/permissions for these connectors.
  const isPerToolPermsEnabled = entry.permissionMode !== 'all_or_nothing';
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Permissions state — initialized from server. While loading we render
  // a quiet skeleton (no spinner) so the modal doesn't visually shift.
  const [permissions, setPermissions] = useState<ConnectorPermissionsResponse | null>(null);
  const [permError, setPermError] = useState<string | null>(null);
  // Tool list collapses past N entries; "View more" expands the rest in place.
  // Default of 3 matches the visual density of the collapsed state.
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const COLLAPSED_TOOL_COUNT = 3;

  useEffect(() => {
    if (closeRef.current) closeRef.current.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    // Permissions only exist for already-connected connectors. Skip the
    // fetch when we're in the connect-CTA state to avoid a guaranteed
    // 404 in the network log.
    if (!isConnected) return;
    // Skip the GET — the server returns 400 for these and the UI doesn't
    // render the grid anyway. Saves a network round-trip + a spurious
    // error banner on every modal open.
    if (!isPerToolPermsEnabled) return;
    let cancelled = false;
    connectorsAPI
      .getPermissions(entry.id)
      .then((res) => {
        if (!cancelled) setPermissions(res.data);
      })
      .catch(() => {
        if (!cancelled) setPermError('Could not load permissions.');
      });
    return () => {
      cancelled = true;
    };
  }, [entry.id, isConnected]);

  // Optimistic permission write. We snapshot the pre-change state, apply
  // the new state synchronously, fire the PUT, and revert + surface the
  // error if the server rejects. Conflicts beyond the modal session are
  // rare since these endpoints are single-user.
  const writePermissions = useCallback(
    async (next: ConnectorPermissionsResponse) => {
      const prev = permissions;
      setPermissions(next);
      setPermError(null);
      try {
        const res = await connectorsAPI.updatePermissions(entry.id, {
          globalPermission: next.globalPermission,
          toolPermissions: next.toolPermissions,
        });
        setPermissions(res.data);
      } catch {
        if (prev) setPermissions(prev);
        setPermError("Couldn't save that change. Try again.");
      }
    },
    [entry.id, permissions]
  );

  const setGlobal = (value: ConnectorPermissionState) => {
    if (!permissions) return;
    void writePermissions({ ...permissions, globalPermission: value });
  };

  const setToolPerm = (tool: string, value: ConnectorPermissionState) => {
    if (!permissions) return;
    void writePermissions({
      ...permissions,
      toolPermissions: { ...permissions.toolPermissions, [tool]: value },
    });
  };

  const copy = CONNECTOR_COPY[entry.id];
  const tools: ConnectorToolDescriptor[] = permissions?.tools ?? [];
  const defaultPerm: ConnectorPermissionState = permissions?.defaultPermission ?? 'ask';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="manage-panel-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="manage-connection-panel"
    >
      <div className="rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-2xl mb-0 sm:my-4 bg-[#1a1a1a] border border-white/10 max-h-[90dvh] sm:max-h-[calc(100dvh-2rem)] overflow-y-auto">
        {/* Header. The breadcrumb "Connectors / <Name>" lets users hop
            back to the overview without losing the modal. onBack is
            optional so older test fixtures (and any non-modal callsite)
            still render — when missing, only the X close is visible. */}
        <div className="flex items-start gap-3 p-6 sm:p-7 border-b border-white/[0.08]">
          <img
            src={entry.brandIcon}
            alt=""
            aria-hidden="true"
            className="w-10 h-10 rounded-lg bg-white/90 p-1.5 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            {onBack && (
              <nav aria-label="Breadcrumb" className="mb-1">
                <ol className="flex items-center gap-1.5 text-xs text-white/50">
                  <li>
                    <button
                      type="button"
                      onClick={onBack}
                      data-testid="manage-breadcrumb-back"
                      className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                    >
                      Connectors
                    </button>
                  </li>
                  <li aria-hidden="true" className="text-white/30">
                    /
                  </li>
                  <li aria-current="page" className="text-white/70">
                    {entry.displayName}
                  </li>
                </ol>
              </nav>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <h2
                id="manage-panel-title"
                className="text-lg font-semibold text-white"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {entry.displayName}
              </h2>
              {/* Header badge — only for OAuth connectors. Universal-key
                  connectors render their own status badge inline so the
                  header doesn't lie during a brief race between enable
                  and connections refetch. */}
              {isConnectMode && (byoStatus === 'connected' || byoStatus === 'error') && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
                  Connected
                </span>
              )}
              {isConnectMode && (byoStatus === 'needs_reauth' || byoStatus === 'needs_setup') && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 text-xs font-medium">
                  {NEEDS_RECONNECT_PILL}
                </span>
              )}
              {isConnectMode && (!byoStatus || byoStatus === 'disconnected') && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-xs font-medium">
                  Not connected
                </span>
              )}
              {!isConnectMode && !isUniversalKey && isConnected && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
                  Enabled
                </span>
              )}
              {!isConnectMode && !isUniversalKey && !isConnected && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-xs font-medium">
                  Not connected
                </span>
              )}
              {!isConnectMode && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 text-white/70 text-[10px] font-semibold tracking-wide">
                  MCP
                </span>
              )}
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center max-md:min-h-11 max-md:min-w-11 text-white/60 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 rounded p-1"
            data-testid="manage-close"
          >
            ✕
          </button>
        </div>

        {/* Body splits here on connection state. The connect-CTA body
            is its own component so this file isn't threaded with null
            checks; the existing permissions/disconnect UI below renders
            unchanged for connected users. */}
        {isConnectMode && (
          <ByoConnectionBody
            entry={entry}
            connection={connection}
            connectResult={connectResult}
            onConnectionChange={onConnectionChange}
          />
        )}
        {!isConnectMode && (isUniversalKey || !isConnected) && (
          <ManageConnectionConnectingState entry={entry} />
        )}
        {isConnected && (
          <>
        {/* Action row: Disconnect. Workspace-scope toggle intentionally
            omitted — founding.dev doesn't have a workspace concept yet,
            so showing a toggle would mislead the user. Add when workspaces
            ship. */}
        <div className="px-6 sm:px-7 pt-4 flex flex-wrap items-center gap-2 justify-end">
          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="inline-flex items-center px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="manage-disconnect-start"
            >
              Disconnect
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={isDisconnecting}
                className="inline-flex items-center px-3 py-2 rounded-lg border border-white/15 text-white/80 hover:bg-white/5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-60"
                data-testid="manage-disconnect-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  void onDisconnect();
                }}
                disabled={isDisconnecting}
                className="inline-flex items-center px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-60"
                data-testid="manage-disconnect-confirm"
              >
                {isDisconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-6 sm:p-7 space-y-6">
          {feedback && (
            <div
              role="status"
              className={`px-3 py-2 rounded-md text-sm ${
                feedback.kind === 'warn'
                  ? 'bg-amber-500/10 text-amber-200 border border-amber-500/30'
                  : 'bg-white/5 text-white/80 border border-white/10'
              }`}
              data-testid="manage-feedback"
            >
              {feedback.text}
            </div>
          )}

          {/* Description */}
          <p className="text-sm text-white/75 leading-relaxed">
            {copy?.description ?? entry.tagline}
          </p>

          {/* Features */}
          {copy?.features?.length ? (
            <div>
              <h3
                className="text-sm font-semibold text-white mb-2"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                With {entry.displayName} MCP, you can:
              </h3>
              <ul className="text-sm text-white/75 space-y-1.5 list-disc pl-5">
                {copy.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Permissions — only renders for per_tool connectors. all_or_nothing
              connectors (universal-key HeyGen) get a single-line inline note
              instead; the per-tool grid would be meaningless because the
              server owns the universal API key and enabling the connector
              already implies blanket consent. */}
          {!isPerToolPermsEnabled && (
            <div
              className="border-t border-white/[0.08] pt-5"
              data-testid="manage-all-or-nothing-note"
            >
              <p className="text-xs text-white/55 leading-relaxed">
                All {entry.displayName} tools are available when this connector is enabled.
              </p>
            </div>
          )}
          {isPerToolPermsEnabled && (
          <div className="border-t border-white/[0.08] pt-5">
            <h3
              className="text-sm font-semibold text-white mb-4"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Manage permissions
            </h3>

            {permError && (
              <div
                role="alert"
                className="mb-3 px-3 py-2 rounded-md text-sm bg-amber-500/10 text-amber-200 border border-amber-500/30"
                data-testid="perm-error"
              >
                {permError}
              </div>
            )}

            {/* Manage all */}
            <div className="flex items-start justify-between gap-3 py-3" data-testid="perm-global-row">
              <div className="min-w-0">
                <div className="text-sm font-medium text-white">Manage all permissions</div>
                <div className="text-xs text-white/55 mt-0.5">
                  Set the default permission for all tools at once
                </div>
              </div>
              <div className="relative flex-shrink-0">
                <select
                  value={permissions?.globalPermission ?? defaultPerm}
                  onChange={(e) => setGlobal(e.target.value as ConnectorPermissionState)}
                  disabled={!permissions}
                  data-testid="perm-global-select"
                  aria-label="Manage all permissions"
                  className="appearance-none bg-white/5 border border-white/10 text-white text-sm rounded-lg pl-3 pr-8 py-1.5 max-md:min-h-11 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {PERMISSION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[#1a1a1a]">
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon
                  className="w-4 h-4 text-white/50 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            </div>

            {/* Per-tool — collapsed to the first COLLAPSED_TOOL_COUNT rows
                so connectors with large catalogs (HeyGen has 43) don't make
                the modal feel infinite. "View more" inlines the rest;
                "View less" rewinds. Tools with a non-default permission are
                always shown so the user can see what they've already set
                even when collapsed. */}
            <div className="mt-2 divide-y divide-white/[0.06] border-t border-white/[0.06]">
              {tools.length === 0 ? (
                <div className="py-4 text-sm text-white/55" data-testid="perm-empty">
                  No tools yet — they'll appear once the agent uses {entry.displayName}.
                </div>
              ) : (
                (() => {
                  // Bring any tool with a per-tool override forward so it's
                  // visible in the collapsed state. Stable ordering otherwise.
                  const overridden = new Set(
                    Object.keys(permissions?.toolPermissions || {})
                  );
                  const ordered = [
                    ...tools.filter((t) => overridden.has(t.name)),
                    ...tools.filter((t) => !overridden.has(t.name)),
                  ];
                  const visible = toolsExpanded
                    ? ordered
                    : ordered.slice(0, COLLAPSED_TOOL_COUNT);
                  const hiddenCount = ordered.length - visible.length;
                  return (
                    <>
                      {visible.map((tool) => {
                        const current =
                          permissions?.toolPermissions?.[tool.name] ??
                          permissions?.globalPermission ??
                          defaultPerm;
                        return (
                          <div
                            key={tool.name}
                            className="flex items-start justify-between gap-3 py-3"
                            data-testid={`perm-tool-row-${tool.name}`}
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-mono text-white">
                                {tool.name}
                              </div>
                              {tool.description && (
                                <div className="text-xs text-white/55 mt-0.5">
                                  {tool.description}
                                </div>
                              )}
                            </div>
                            <div className="flex-shrink-0">
                              <PermissionSegmented
                                value={current}
                                onChange={(v) => setToolPerm(tool.name, v)}
                                ariaLabelPrefix={tool.name}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {ordered.length > COLLAPSED_TOOL_COUNT && (
                        <div className="py-3 flex justify-center">
                          <button
                            type="button"
                            onClick={() => setToolsExpanded((v) => !v)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white/80 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.10] transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                            data-testid="perm-tools-toggle"
                          >
                            {toolsExpanded
                              ? 'View less'
                              : `+ View more (${hiddenCount})`}
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default ManageConnectionPanel;
